// Real HTTP adapter for FEATURE-048 (Role Management) — talks to admin-api at /api/v1/admin/roles.
// Selected over the in-memory mock when VITE_USE_MOCK_API=false (see role.adapter.ts); the
// production/docker env files set that flag, so this is the live deployment path.
// Maps the server error envelope { error: { code, message, fields } } to RoleApiError.

import { endpoints } from '@/api/endpoints';
import { httpClient } from '@/api/http-client';
import {
  RoleApiError,
  type CreateRoleInput,
  type DeleteRoleResult,
  type Role,
  type RoleApi,
  type UpdateRoleInput,
} from '../types/role.types';

interface ApiEnvelope<T> {
  data: T;
}

interface ServerErrorEnvelope {
  error?: {
    code?: string;
    message?: string;
    fields?: Record<string, string>;
  };
}

// A failure with no server envelope (5xx body-less, network, gateway HTML) carries NO message —
// `getRoleErrorMessage` prefers `e.message` over its fallback key, so a generic default here would
// shadow the operation-specific PRD §8.2 copy ("저장에 실패했습니다." / "삭제에 실패했습니다.").
// Leave it empty and let the caller's fallbackKey win (SHCT-631).
function mapError(e: unknown): never {
  const candidate = e as { response?: { data?: ServerErrorEnvelope } };
  const payload = candidate.response?.data?.error;
  throw new RoleApiError(
    (payload?.code as RoleApiError['code']) ?? 'UNKNOWN',
    payload?.message ?? '',
    payload?.fields,
  );
}

export const roleHttpApi: RoleApi = {
  async listRoles() {
    try {
      const res = await httpClient.get<ApiEnvelope<{ items: Role[] }>>(endpoints.roles);
      if (!Array.isArray(res.data?.data?.items)) {
        throw new RoleApiError('UNKNOWN', '역할 목록을 불러올 수 없습니다.');
      }
      return res.data.data.items;
    } catch (e) {
      mapError(e);
    }
  },

  async getRole(id) {
    try {
      const res = await httpClient.get<ApiEnvelope<Role>>(`${endpoints.roles}/${id}`);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async createRole(input: CreateRoleInput) {
    try {
      const res = await httpClient.post<ApiEnvelope<Role>>(endpoints.roles, input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async updateRole(id, input: UpdateRoleInput) {
    try {
      const res = await httpClient.patch<ApiEnvelope<Role>>(`${endpoints.roles}/${id}`, input);
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },

  async deleteRole(id): Promise<DeleteRoleResult> {
    try {
      const res = await httpClient.delete<ApiEnvelope<DeleteRoleResult>>(
        `${endpoints.roles}/${id}`,
      );
      return res.data.data;
    } catch (e) {
      mapError(e);
    }
  },
};
